<template>
  <template v-if="total || showEmpty">
    <f7-block-title
      >Karty na vrátenie<span v-if="total"> ({{ total }})</span></f7-block-title
    >
    <f7-block strong inset v-if="!total">
      <p class="text-align-center">Všetky parkovacie karty máte vrátené.</p>
    </f7-block>
    <f7-list v-else media-list strong inset outline-ios dividers-ios>
      <f7-list-item v-for="loan in loans" :key="loan.id" :title="formatDate(loan.date)">
        <template #after>
          <f7-button
            small
            :disabled="!!busyId || loan.verificationPolicy !== 'self_declared_v1'"
            @click="confirmReturn(loan)"
          >
            {{ busyId === loan.id ? 'Potvrdzujem…' : 'Vrátiť kartu' }}
          </f7-button>
        </template>
        <template #footer>
          <div>Vrátiť do {{ formatDeadline(loan.returnDueAt) }}</div>
          <div v-if="loan.cancelled">Rezervácia bola zrušená.</div>
          <div v-if="loan.verificationPolicy !== 'self_declared_v1'">
            Vrátenie vyžaduje dodatočné overenie správcom.
          </div>
        </template>
      </f7-list-item>
    </f7-list>
    <f7-block v-if="loans.length < total">
      <f7-button :disabled="loadingMore" @click="loadMore">{{
        loadingMore ? 'Načítavam…' : 'Načítať ďalšie karty'
      }}</f7-button>
    </f7-block>
  </template>
</template>

<script setup lang="ts">
import { f7, useStore } from 'framework7-vue'
import { DateTime } from 'luxon'
import { ref, type ComputedRef } from 'vue'

import Reservations from '@/assets/classes/parking/Reservations'
import { refreshBanStatus } from '@/assets/composables/ban'
import { returnCardLoan } from '@/assets/composables/endpoints'
import { showPreloader } from '@/assets/composables/preloader'
import type { CardLoanData } from '@/assets/composables/types'
import { notify, processError } from '@/assets/composables/utils'
import store from '@/js/store'

defineProps({ showEmpty: { type: Boolean, default: false } })
const loans = useStore('cardLoans') as ComputedRef<CardLoanData[]>
const total = useStore('cardLoanTotal') as ComputedRef<number>
const busyId = ref<string | null>(null)
const loadingMore = ref(false)
const formatDate = (date: string) => DateTime.fromISO(date).toFormat('dd. MM. yyyy')
const formatDeadline = (date: string) =>
  DateTime.fromISO(date).toLocal().toFormat('dd. MM. yyyy HH:mm')
const submitReturn = async (loan: CardLoanData) => {
  if (busyId.value) return
  busyId.value = loan.id
  const closePreloader = showPreloader('Potvrdzujem vrátenie karty')
  try {
    await returnCardLoan(loan.id)
    await store.dispatch('removeCardLoan', loan.id)
    await Promise.all([Reservations.fetchNewData(), refreshBanStatus()])
    notify('Vrátenie parkovacej karty bolo potvrdené.')
  } catch (error) {
    await processError(error)
    await Reservations.fetchCardLoans()
  } finally {
    busyId.value = null
    closePreloader()
  }
}
const confirmReturn = (loan: CardLoanData) => {
  f7.dialog.confirm(
    `Potvrdzujete, že ste fyzicky odovzdali parkovaciu kartu k rezervácii na <b>${formatDate(loan.date)}</b>?`,
    () => submitReturn(loan),
  )
}
const loadMore = async () => {
  if (loadingMore.value) return
  loadingMore.value = true
  try {
    await Reservations.fetchCardLoans(true)
  } finally {
    loadingMore.value = false
  }
}
</script>
